import React, { useState, useEffect } from 'react';
import { useNavigation } from './src/navigation';

interface QuickActionsProps {
  showQuickPurchasePopup?: boolean;
  onShowQuickPurchasePopup?: (show: boolean) => void;
}

const QuickActions: React.FC<QuickActionsProps> = ({ 
  showQuickPurchasePopup: externalShowPopup, 
  onShowQuickPurchasePopup 
}) => {
  const { navigateTo } = useNavigation();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [internalShowPopup, setInternalShowPopup] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const showQuickPurchasePopup = externalShowPopup !== undefined ? externalShowPopup : internalShowPopup;
  const setShowQuickPurchasePopup = onShowQuickPurchasePopup || setInternalShowPopup;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const checkTablet = () => {
      setIsTablet(window.innerWidth < 1024);
    };
    checkTablet();
    window.addEventListener('resize', checkTablet);
    return () => window.removeEventListener('resize', checkTablet);
  }, []);

  return (
    <div style={{
      flex: showQuickPurchasePopup ? '1' : (isTablet ? '1' : '0 0 280px'),
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '0.75rem' : '1rem',
      maxHeight: isTablet ? 'none' : '100vh',
      overflow: showQuickPurchasePopup ? 'auto' : 'hidden',
      width: showQuickPurchasePopup ? '100%' : (isTablet ? '100%' : '280px')
    }}>
      {/* Side Header - Hide when form is shown */}
      {!showQuickPurchasePopup && (
        <div style={{
          textAlign: 'center',
          marginBottom: isMobile ? '0.25rem' : '0.5rem'
        }}>
          <h2 style={{
            fontSize: isMobile ? '1rem' : '1.1rem',
            fontWeight: '700',
            color: '#1f2937',
            margin: '0 0 0.5rem 0',
            letterSpacing: '-0.02em'
          }}>
            Quick Actions
          </h2>
          <div style={{
            width: isMobile ? '40px' : '50px',
            height: '2px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '1px',
            margin: '0 auto'
          }}></div>
        </div>
      )}

      {/* Quick Purchase Button - Hide when form is shown */}
      {!showQuickPurchasePopup && (
      <div 
        onClick={() => setShowQuickPurchasePopup(true)}
        style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(239, 246, 255, 0.95) 100%)',
        backdropFilter: 'blur(25px)',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '1rem' : '1.5rem',
        border: '1px solid rgba(59, 130, 246, 0.15)',
        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '0.75rem' : '1rem',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(59, 130, 246, 0.2)';
        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.25)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.1)';
        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.15)';
      }}>
        <div style={{
          width: isMobile ? '40px' : '50px',
          height: isMobile ? '40px' : '50px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
          borderRadius: isMobile ? '10px' : '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: '0 6px 16px rgba(59, 130, 246, 0.3)',
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <svg width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 5v14M5 12h14"/>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '0.25rem',
            letterSpacing: '-0.02em'
          }}>
            Quick Purchase
          </div>
          <div style={{
            fontSize: isMobile ? '0.7rem' : '0.75rem',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            Add new medications
          </div>
        </div>
      </div>
      )}

      {/* Purchase History - Hide when form is shown */}
      {!showQuickPurchasePopup && (
      <div 
        onClick={() => navigateTo('purchase-history')}
        style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
        backdropFilter: 'blur(25px)',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '1rem' : '1.5rem',
        border: '1px solid rgba(16, 185, 129, 0.15)',
        boxShadow: '0 8px 24px rgba(16, 185, 129, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '0.75rem' : '1rem',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(16, 185, 129, 0.2)';
        e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.25)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.1)';
        e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.15)';
      }}>
        <div style={{
          width: isMobile ? '40px' : '50px',
          height: isMobile ? '40px' : '50px',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
          borderRadius: isMobile ? '10px' : '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: '0 6px 16px rgba(16, 185, 129, 0.3)',
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <svg width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6"/>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '0.25rem',
            letterSpacing: '-0.02em'
          }}>
            Purchase History
          </div>
          <div style={{
            fontSize: isMobile ? '0.7rem' : '0.75rem',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            View past purchases
          </div>
        </div>
      </div>
      )}

      {/* Sales History - Hide when form is shown */}
      {!showQuickPurchasePopup && (
      <div 
        onClick={() => navigateTo('sales-history')}
        style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(254, 242, 242, 0.95) 100%)',
        backdropFilter: 'blur(25px)',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '1rem' : '1.5rem',
        border: '1px solid rgba(239, 68, 68, 0.15)',
        boxShadow: '0 8px 24px rgba(239, 68, 68, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '0.75rem' : '1rem',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(239, 68, 68, 0.2)';
        e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.25)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(239, 68, 68, 0.1)';
        e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.15)';
      }}>
        <div style={{
          width: isMobile ? '40px' : '50px',
          height: isMobile ? '40px' : '50px',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)',
          borderRadius: isMobile ? '10px' : '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: '0 6px 16px rgba(239, 68, 68, 0.3)',
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <svg width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M9 19c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9zM21 3l-2 2m-7 7l2 2"/>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '0.25rem',
            letterSpacing: '-0.02em'
          }}>
            Sales History
          </div>
          <div style={{
            fontSize: isMobile ? '0.7rem' : '0.75rem',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            View sales records
          </div>
        </div>
      </div>
      )}

      {/* Distributors - Hide when form is shown */}
      {!showQuickPurchasePopup && (
      <div 
        onClick={() => navigateTo('distributors-history')}
        style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 251, 235, 0.95) 100%)',
        backdropFilter: 'blur(25px)',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '1rem' : '1.5rem',
        border: '1px solid rgba(245, 158, 11, 0.15)',
        boxShadow: '0 8px 24px rgba(245, 158, 11, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '0.75rem' : '1rem',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(245, 158, 11, 0.2)';
        e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.25)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(245, 158, 11, 0.1)';
        e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.15)';
      }}>
        <div style={{
          width: isMobile ? '40px' : '50px',
          height: isMobile ? '40px' : '50px',
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)',
          borderRadius: isMobile ? '10px' : '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: '0 6px 16px rgba(245, 158, 11, 0.3)',
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <svg width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '0.25rem',
            letterSpacing: '-0.02em'
          }}>
            Distributors
          </div>
          <div style={{
            fontSize: isMobile ? '0.7rem' : '0.75rem',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            Manage suppliers
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default QuickActions;
